import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";

type Release = {
  _id: string;
  version: string;
  track: "stable" | "beta" | string;
  url: string;
  sha256: string;
  sizeBytes: number;
  releaseNotes?: string;
  createdAt?: string;
};

const Wrap = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 1.5rem 1rem 4rem;
  color: #e0e0e0;
  font-family: "JetBrains Mono", "Courier New", monospace;
  @media (max-width: 700px) {
    padding: 12px 8px 3rem;
  }
`;
const Card = styled.div`
  background: #141414;
  border: 1px solid #232323;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1rem;
  @media (max-width: 700px) {
    padding: 0.85rem;
  }
`;
const Title = styled.h1`
  font-size: 1.2rem;
  margin: 0 0 0.5rem;
`;
const Sub = styled.div`
  color: #888;
  font-size: 0.85rem;
  margin-bottom: 1.25rem;
`;
const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.75rem;
`;
const Label = styled.label`
  color: #888;
  font-size: 0.75rem;
`;
const Input = styled.input`
  background: #0d0d0d;
  color: #e0e0e0;
  border: 1px solid #2a2a2a;
  border-radius: 3px;
  padding: 0.5rem 0.6rem;
  font-family: inherit;
  font-size: 0.9rem;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
`;
const Select = styled.select`
  background: #0d0d0d;
  color: #e0e0e0;
  border: 1px solid #2a2a2a;
  border-radius: 3px;
  padding: 0.5rem 0.6rem;
  font-family: inherit;
  font-size: 0.9rem;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
`;
const Btn = styled.button`
  background: #e0e0e0;
  color: #111;
  border: none;
  padding: 0.55rem 0.9rem;
  border-radius: 3px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.85rem;
  @media (max-width: 700px) {
    width: 100%;
  }
`;
const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  @media (max-width: 700px) {
    margin: 0 -8px;
  }
`;
const ReleasesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  @media (max-width: 700px) {
    min-width: 520px;
  }
`;
const Th = styled.th`
  padding: 0.4rem;
  text-align: left;
  color: #888;
`;
const Td = styled.td`
  padding: 0.4rem;
`;
const Mono = styled.code`
  font-family: inherit;
  font-size: 0.8rem;
  color: #ccc;
`;

export default function AotdIrlAdminFirmware() {
  const user = useAppSelector((s) => s.auth);
  const isAdmin = checkRole(user.role || "", "admin");
  const [releases, setReleases] = useState<Release[]>([]);
  const [version, setVersion] = useState("");
  const [track, setTrack] = useState<"stable" | "beta">("stable");
  const [url, setUrl] = useState("");
  const [sha256, setSha256] = useState("");
  const [sizeBytes, setSizeBytes] = useState<string>("");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get("/api/aotd-irl/admin/firmware");
      setReleases(Array.isArray(r.data?.releases) ? r.data.releases : []);
    } catch {
      // best effort
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!version || !url || !sha256 || !sizeBytes) {
        toast.error("version, url, sha256, sizeBytes are required");
        return;
      }
      const sz = Number(sizeBytes);
      if (!Number.isFinite(sz) || sz <= 0) {
        toast.error("sizeBytes must be a positive number");
        return;
      }
      setLoading(true);
      try {
        await apiClient.post("/api/aotd-irl/admin/firmware", {
          version: version.trim(),
          track,
          url: url.trim(),
          sha256: sha256.trim().toLowerCase(),
          sizeBytes: Math.floor(sz),
          releaseNotes: releaseNotes.trim() || undefined,
        });
        toast.success(`registered ${version} (${track})`);
        setVersion("");
        setUrl("");
        setSha256("");
        setSizeBytes("");
        setReleaseNotes("");
        await load();
      } catch (err) {
        const ax = err as { response?: { data?: { error?: string } }; message?: string };
        toast.error(ax?.response?.data?.error || ax?.message || "failed to register");
      } finally {
        setLoading(false);
      }
    },
    [version, track, url, sha256, sizeBytes, releaseNotes, load]
  );

  if (!isAdmin) {
    return (
      <Wrap>
        <Title>aotd-irl firmware</Title>
        <Sub>admin only</Sub>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Link to="/aotd-irl/admin" style={{ color: "#888" }}>
        ← aotd-irl admin
      </Link>
      <Title>firmware releases</Title>
      <Sub>
        Register a firmware release after uploading the binary to GCS (or any
        permanent URL). Devices download from <Mono>url</Mono> and verify <Mono>sha256</Mono>.
      </Sub>

      <Card>
        <h2 style={{ fontSize: "1rem", margin: "0 0 0.75rem" }}>register a release</h2>
        <form onSubmit={submit}>
          <FieldRow>
            <Label>version (e.g. aotd-irl-2.3.0)</Label>
            <Input value={version} onChange={(e) => setVersion(e.target.value)} />
          </FieldRow>
          <FieldRow>
            <Label>track</Label>
            <Select value={track} onChange={(e) => setTrack(e.target.value as "stable" | "beta")}>
              <option value="stable">stable</option>
              <option value="beta">beta</option>
            </Select>
          </FieldRow>
          <FieldRow>
            <Label>url (https)</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} />
          </FieldRow>
          <FieldRow>
            <Label>sha256 (64 hex chars)</Label>
            <Input value={sha256} onChange={(e) => setSha256(e.target.value)} />
          </FieldRow>
          <FieldRow>
            <Label>size in bytes</Label>
            <Input
              type="number"
              value={sizeBytes}
              onChange={(e) => setSizeBytes(e.target.value)}
            />
          </FieldRow>
          <FieldRow>
            <Label>release notes</Label>
            <Input value={releaseNotes} onChange={(e) => setReleaseNotes(e.target.value)} />
          </FieldRow>
          <Btn type="submit" disabled={loading}>
            {loading ? "registering..." : "register"}
          </Btn>
        </form>
      </Card>

      <Card>
        <h2 style={{ fontSize: "1rem", margin: "0 0 0.75rem" }}>existing releases</h2>
        {releases.length === 0 ? (
          <Sub>no releases registered yet.</Sub>
        ) : (
          <TableWrap>
            <ReleasesTable>
              <thead>
                <tr>
                  <Th>version</Th>
                  <Th>track</Th>
                  <Th>size</Th>
                  <Th>sha256</Th>
                  <Th>created</Th>
                </tr>
              </thead>
              <tbody>
                {releases.map((r) => (
                  <tr key={r._id} style={{ borderTop: "1px solid #232323" }}>
                    <Td>
                      <Mono>{r.version}</Mono>
                    </Td>
                    <Td>{r.track}</Td>
                    <Td>{(r.sizeBytes / 1024).toFixed(1)} KB</Td>
                    <Td style={{ color: "#666", fontSize: ".7rem" }}>
                      {r.sha256.slice(0, 12)}…
                    </Td>
                    <Td style={{ color: "#888" }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </ReleasesTable>
          </TableWrap>
        )}
      </Card>
    </Wrap>
  );
}
